"use client";

import { Calendar, User, ArrowLeft, Clock, Tag } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="19404" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-6">
          <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
          <div className="h-12 w-3/4 bg-muted animate-pulse rounded-md" />
          <div className="h-6 w-1/2 bg-muted animate-pulse rounded-md" />
          <div className="h-96 w-full bg-muted animate-pulse rounded-lg" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-full bg-muted animate-pulse rounded-md" />
            <div className="h-4 w-5/6 bg-muted animate-pulse rounded-md" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <p className="text-muted-foreground">Failed to load article.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <p className="text-muted-foreground">Article not found.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const readingTime = post.content
    ? Math.max(1, Math.ceil(post.content.replace(/<[^>]*>/g, "").split(/\s+/).length / 200))
    : 1;

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      {}
      <div className="mb-8">
        <Link to="/NewsroomPage" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to newsroom
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center px-3 py-1 text-xs font-medium uppercase tracking-wider bg-secondary text-secondary-foreground rounded-lg"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-6 leading-tight">
        {post.title}
      </h1>

      {}
      {post.excerpt && (
        <div
          className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8 font-serif"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      <div className="flex flex-wrap items-center gap-6 pb-8 mb-8 border-b border-border">
        {post.author && (
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              {post.author.avatar && (
                <AvatarImage src={post.author.avatar["96"] || post.author.avatar["48"]} alt={post.author.name} />
              )}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                {post.author.name?.charAt(0) || "A"}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Reported by</span>
              <span className="text-sm font-medium text-foreground">{post.author.name}</span>
            </div>
          </div>
        )}

        {formattedDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            <span>{formattedDate}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>{readingTime} min read</span>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <figure className="mb-10 md:mb-12">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto rounded-lg shadow-md object-cover"
            loading="lazy"
            decoding="async"
          />
        </figure>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-lg max-w-none text-foreground leading-relaxed [&>p]:mb-6 [&>p]:text-base [&>p]:md:text-lg [&>p]:font-serif [&>p]:text-foreground/90 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:md:text-4xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-2xl [&>h3]:md:text-3xl [&>h3]:font-semibold [&>h3]:mt-10 [&>h3]:mb-4 [&>h3]:text-foreground [&>h4]:font-default [&>h4]:text-xl [&>h4]:font-semibold [&>h4]:mt-8 [&>h4]:mb-3 [&>h4]:text-foreground [&>ul]:my-6 [&>ul]:pl-6 [&>ul>li]:mb-2 [&>ul>li]:list-disc [&>ul>li]:font-serif [&>ol]:my-6 [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li]:list-decimal [&>ol>li]:font-serif [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:my-8 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:font-serif [&>img]:rounded-lg [&>img]:my-8 [&>img]:w-full [&>img]:h-auto [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:opacity-80 [&>pre]:bg-secondary [&>pre]:p-4 [&>pre]:rounded-lg [&>pre]:overflow-x-auto [&>pre]:my-6 [&>pre]:font-mono [&>pre]:text-sm [&>code]:bg-secondary [&>code]:px-1.5 [&>code]:py-0.5 [&>code]:rounded-md [&>code]:font-mono [&>code]:text-sm"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Tag className="w-4 h-4" />
              <span>Topics:</span>
            </div>
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="inline-flex items-center px-3 py-1 text-xs font-medium bg-secondary text-secondary-foreground rounded-lg hover:bg-accent transition-colors"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <Separator className="my-12" />

      {}
      {post.author && (
        <Card className="bg-secondary/20 border-border rounded-lg shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <Avatar className="h-16 w-16 shrink-0">
                {post.author.avatar && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                  {post.author.name?.charAt(0) || "A"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  <User className="w-3 h-3" />
                  <span>About the journalist</span>
                </div>
                <h3 className="font-default text-xl font-semibold text-foreground mb-2">{post.author.name}</h3>
                <p className="text-sm text-muted-foreground font-serif leading-relaxed">
                  Dedicated reporter at Inclusive Voices News, committed to uncovering the truth and amplifying the stories that shape our community.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {}
      <div className="mt-12 text-center">
        <Link to="/NewsroomPage">
          <Button variant="outline" size="lg" className="rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to all news
          </Button>
        </Link>
      </div>
    </article>
  );
}

export default function () {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}